import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Wallet, CreditCard, Bitcoin, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PACKAGES = [
  { id: 'starter', name: 'Starter Pack', tokens: 10, price: 10.00, popular: false },
  { id: 'pro', name: 'Pro Pack', tokens: 25, price: 22.50, popular: true },
  { id: 'enterprise', name: 'Enterprise Pack', tokens: 50, price: 40.00, popular: false }
];

const Deposit = () => {
  const { user, refreshUser } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState(PACKAGES[1]);
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const response = await axios.post(
        `${API_URL}/api/payments/create-checkout`,
        null,
        {
          params: {
            package_id: selectedPackage.id,
            origin_url: origin
          },
          withCredentials: true
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create checkout session';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Deposit Tokens
          </h1>
          <p className="text-zinc-500 mt-2">
            Purchase tokens to render cards. Pay with card or crypto.
          </p>
        </div>

        {/* Current Balance */}
        <div className="glass rounded-xl p-6 text-center">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Current Balance</p>
          <p className="text-4xl font-bold text-amber-400 font-mono" data-testid="deposit-token-balance">
            {user?.tokens?.toLocaleString() || 0}
          </p>
          <p className="text-zinc-500 text-sm mt-1">tokens</p>
        </div>

        {/* Packages */}
        <div className="grid md:grid-cols-3 gap-6">
          {PACKAGES.map((pkg, index) => (
            <button
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg)}
              data-testid={`package-${pkg.id}`}
              className={`relative glass rounded-xl p-6 text-left transition-all duration-200 animate-fadeIn ${
                selectedPackage.id === pkg.id
                  ? 'border-2 border-amber-500 ring-2 ring-amber-500/20'
                  : 'border border-white/10 hover:border-white/20'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500 text-black text-xs font-semibold rounded-full">
                    <Sparkles className="w-3 h-3" />
                    Popular
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-white">{pkg.name}</h3>
                {selectedPackage.id === pkg.id && (
                  <Check className="w-5 h-5 text-amber-400" />
                )}
              </div>

              <div className="space-y-2">
                <p className="text-3xl font-bold text-white font-mono">
                  {pkg.tokens}
                  <span className="text-zinc-500 text-base font-normal ml-2">tokens</span>
                </p>
                <p className="text-2xl font-semibold text-amber-400">
                  ${pkg.price.toFixed(2)}
                </p>
                <p className="text-zinc-500 text-sm">
                  ${(pkg.price / pkg.tokens).toFixed(2)} per token
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* BTC Wallet Info */}
        {user?.btc_wallet && (
          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <Bitcoin className="w-5 h-5 text-amber-400" />
              <h3 className="font-heading text-lg font-semibold text-white">Your BTC Wallet</h3>
            </div>
            <p className="font-mono text-sm text-zinc-400 break-all bg-white/5 p-3 rounded-lg" data-testid="btc-wallet">
              {user.btc_wallet}
            </p>
          </div>
        )}

        {/* Payment Methods */}
        <div className="glass rounded-xl p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4">Payment Methods</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg">
              <CreditCard className="w-5 h-5 text-zinc-400" />
              <span className="text-zinc-300 text-sm">Credit Card</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg">
              <Bitcoin className="w-5 h-5 text-amber-400" />
              <span className="text-zinc-300 text-sm">Crypto (BTC)</span>
            </div>
          </div>
        </div>

        {/* Checkout Button */}
        <div className="flex justify-center">
          <Button
            onClick={handleCheckout}
            disabled={loading}
            data-testid="checkout-btn"
            className="w-full max-w-md h-14 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-lg transition-all duration-200"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing...
              </span>
            ) : (
              <>
                <Wallet className="w-5 h-5 mr-2" />
                Pay ${selectedPackage.price.toFixed(2)} for {selectedPackage.tokens} tokens
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default Deposit;
